import React, { useEffect, useRef, useState } from "react"
import jsep from "jsep"
import { Expr } from "../../shared/Expr"
import { Parameter } from "../../shared/Parameter"

jsep.addBinaryOp("^", 10)

type BinaryOp = "+" | "-" | "*" | "/" | "^" | "%"
type UnaryOp = "sin" | "cos" | "tan" | "abs" | "neg" | "log" | "sqrt" | "floor"

export type ExtendedExpr = Expr<number>

function parseFormulaToExpr(
  formula: string,
  parameters: Record<string, Parameter>,
): ExtendedExpr {
  const expr = jsep(formula.startsWith("=") ? formula.slice(1) : formula)
  return convert(expr, parameters)
}

function convert(
  node: jsep.Expression,
  parameters: Record<string, Parameter>,
): ExtendedExpr {
  switch (node.type) {
    case "Literal":
      return { type: "lit", value: node.value as number }
    case "Identifier":
      if (
        !Object.entries(parameters).find((p) => p[1].name === node.name)?.[0]
      ) {
        throw new Error(`Unknown parameter ${node.name}`)
      }
      return {
        type: "ref",
        id: Object.entries(parameters).find(
          (p) => p[1].name === node.name,
        )?.[0] as string,
      }
    case "BinaryExpression":
      return {
        type: "binary",
        op: node.operator as BinaryOp,
        left: convert(node.left, parameters),
        right: convert(node.right, parameters),
      }
    case "UnaryExpression": {
      const opMap: Record<string, UnaryOp> = {
        "-": "neg",
        "+": "abs",
      }
      return {
        type: "unary",
        op: opMap[node.operator] || (node.operator as UnaryOp),
        operand: convert(node.argument, parameters),
      }
    }
    case "CallExpression": {
      const funcName = (node.callee as jsep.Identifier).name.toLowerCase()
      const unaryOps: UnaryOp[] = [
        "sin",
        "cos",
        "tan",
        "abs",
        "neg",
        "log",
        "sqrt",
        "floor",
      ]
      if (unaryOps.includes(funcName as UnaryOp)) {
        return {
          type: "unary",
          op: funcName as UnaryOp,
          operand: convert(node.arguments[0], parameters),
        }
      }
      throw new Error(`Unsupported function: ${funcName}`)
    }
    default:
      throw new Error("Unsupported expression node: " + node.type)
  }
}

function exprToFormulaString(
  expr: Expr<number>,
  parameters: Record<string, Parameter>,
): string {
  switch (expr.type) {
    case "lit":
      return typeof expr.value === "string"
        ? `"${expr.value}"`
        : expr.value.toString()
    case "ref":
      return parameters[expr.id]?.name ?? expr.id
    case "binary":
      return `(${exprToFormulaString(expr.left, parameters)} ${expr.op} ${exprToFormulaString(expr.right, parameters)})`
    case "unary":
      return `${expr.op}(${exprToFormulaString(expr.operand, parameters)})`
    default:
      return "?"
  }
}

function validateExpr(
  expr: Expr<number>,
  parameters: Record<string, Parameter>,
  highlights: { start: number; end: number; message: string }[],
  raw: string,
) {
  if (expr.type === "binary") {
    const check = (side: "left" | "right", other: "right" | "left") => {
      const a = expr[side]
      const b = expr[other]
      if (
        a?.type === "ref" &&
        b?.type === "lit" &&
        typeof b.value === "string"
      ) {
        const param = parameters[a.id]
        if (
          param.type === "string" &&
          param.options &&
          !param.options.includes(b.value)
        ) {
          const idx = raw.indexOf(`"${b.value}"`)
          if (idx !== -1) {
            highlights.push({
              start: idx,
              end: idx + b.value.length + 2,
              message: `Invalid value ${b.value} for parameter "${param.name}". Allowed: ${param.options.join(", ")}.`,
            })
          }
        }
      }
    }
    if (expr.op === "==" || expr.op === "!=") {
      check("left", "right")
      check("right", "left")
    }

    if (expr.left) validateExpr(expr.left, parameters, highlights, raw)
    if (expr.right) validateExpr(expr.right, parameters, highlights, raw)
  } else if (expr.type === "unary" && expr.operand) {
    validateExpr(expr.operand, parameters, highlights, raw)
  }
}

function renderHighlightedHTML(
  raw: string,
  highlights: { start: number; end: number; message: string }[],
): { html: string; spans: { id: string; message: string }[] } {
  if (!highlights.length) return { html: escapeHTML(raw), spans: [] }

  highlights.sort((a, b) => a.start - b.start)
  let result = ""
  let current = 0
  const spans: { id: string; message: string }[] = []

  for (const { start, end, message } of highlights) {
    const id = `hl_${Math.random().toString(36).slice(2)}`
    result += escapeHTML(raw.slice(current, start))
    const chunk = escapeHTML(raw.slice(start, end))
    result += `<span data-hlid="${id}" data-message="${escapeHTML(message)}" class="squiggle">${chunk}</span>`
    spans.push({ id, message })
    current = end
  }

  result += escapeHTML(raw.slice(current))
  return { html: result, spans }
}

function escapeHTML(str: string) {
  return str.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&":
        return "&amp;"
      case "<":
        return "&lt;"
      case ">":
        return "&gt;"
      case '"':
        return "&quot;"
      case "'":
        return "&#039;"
      default:
        return c
    }
  })
}

type Props = {
  env: Record<string, Expr<number>>
  onSubmit: (expr: Expr<number>) => void
  defaultExpression: Expr<number>
  parameters: Record<string, Parameter>
}

const unaryFunctions: UnaryOp[] = [
  "sin",
  "cos",
  "tan",
  "abs",
  "neg",
  "log",
  "sqrt",
  "floor",
]

function setCaretPosition(el: HTMLElement, offset: number) {
  const range = document.createRange()
  const selection = window.getSelection()
  let currentOffset = 0

  function findNode(node: Node): boolean {
    if (node.nodeType === Node.TEXT_NODE) {
      const length = (node.textContent ?? "").length
      if (currentOffset + length >= offset) {
        range.setStart(node, offset - currentOffset)
        range.collapse(true)
        return true
      }
      currentOffset += length
    } else {
      for (const child of Array.from(node.childNodes)) {
        if (findNode(child)) return true
      }
    }
    return false
  }

  findNode(el)

  selection?.removeAllRanges()
  selection?.addRange(range)
}

const styles = `
  .squiggle {
    text-decoration: red wavy underline;
    position: relative;
    
  }

  .tooltip {
    position: absolute;
    background: #333;
    color: white;
    font-size: 0.8rem;
    padding: 6px 10px;
    border-radius: 4px;
    pointer-events: none;
    white-space: pre-wrap;
    z-index: 1000;
    max-width: 300px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  }
`

export const ExpressionInput: React.FC<Props> = ({
  env,
  onSubmit,
  defaultExpression,
  parameters,
}) => {
  const inputRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<number | null>(null)
  const [html, setHtml] = useState<string>("")

  const parsedExprRef = useRef<Expr<number> | null>(null)

  const [hoveredTooltip, setHoveredTooltip] = useState<{
    message: string
    x: number
    y: number
  } | null>(null)

  function attachTooltipListeners(spans: { id: string; message: string }[]) {
    for (const { id, message } of spans) {
      const el = document.querySelector(`[data-hlid="${id}"]`)
      if (el) {
        el.addEventListener("mouseenter", (e: any) => {
          setHoveredTooltip({
            message,
            x: e.clientX,
            y: e.clientY + 20,
          })
        })
        el.addEventListener("mouseleave", () => {
          setHoveredTooltip(null)
        })
      }
    }
  }

  function handleMouseOver(e: React.MouseEvent<HTMLDivElement>) {
    // const span = (e.target as HTMLElement).closest("[data-hlid]") as HTMLElement
    // const el = inputRef.current
    // if (!span || !el) return
    // // Save caret position
    // // handleChange()
    // const message = span.getAttribute("data-message")
    // if (message) {
    // setHoveredTooltip({
    //   message,
    //   x: e.clientX,
    //   y: e.clientY + 20,
    // })
    // }
  }

  function handleMouseOut(e: React.MouseEvent<HTMLDivElement>) {
    const related = e.relatedTarget as HTMLElement
    const leftFrom = (e.target as HTMLElement).closest("[data-hlid]")
    const enteredTo = related?.closest?.("[data-hlid]")
    if (leftFrom && leftFrom !== enteredTo) {
      setHoveredTooltip(null)
    }
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const el = e.target as HTMLElement
      if (!el.closest("[data-hlid]")) {
        setHoveredTooltip(null)
      }
    }

    document.addEventListener("mousemove", handleMouseMove)
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
    }
  }, [])

  function handleChange() {
    console.log("change")
    const el = inputRef.current
    if (!el) return

    const selection = window.getSelection()
    const range = selection?.getRangeAt(0)
    const preCaretRange = range?.cloneRange()
    preCaretRange?.selectNodeContents(el)
    preCaretRange?.setEnd(range!.endContainer, range!.endOffset)
    const caretOffset = preCaretRange?.toString().length ?? 0

    const text = el.innerText
    try {
      const parsed = parseFormulaToExpr(text, parameters)
      const highlights: { start: number; end: number; message: string }[] = []
      validateExpr(parsed, parameters, highlights, text)
      parsedExprRef.current = parsed

      const { html: newHtml, spans } = renderHighlightedHTML(text, highlights)
      setHtml(newHtml)
      setError(highlights[0]?.message ?? null)
      setResult(Expr.evaluate(parsed, env))

      requestAnimationFrame(() => {
        setCaretPosition(el, caretOffset)
        attachTooltipListeners(spans)
      })
    } catch (err: any) {
      parsedExprRef.current = null
      setError(err.message || "Invalid expression")
      setResult(null)
      const { html: fallbackHtml, spans } = renderHighlightedHTML(text, [])
      setHtml(fallbackHtml)

      requestAnimationFrame(() => {
        setCaretPosition(el, caretOffset)
        attachTooltipListeners(spans)
      })
    }
  }

  useEffect(() => {
    const str = exprToFormulaString(defaultExpression, parameters)
    if (inputRef.current) inputRef.current.innerText = str
    const el = inputRef.current
    if (!el) return
    const text = el.innerText
    try {
      const parsed = parseFormulaToExpr(text, parameters)
      const highlights: { start: number; end: number; message: string }[] = []
      validateExpr(parsed, parameters, highlights, text)
      parsedExprRef.current = parsed

      const { html: newHtml, spans } = renderHighlightedHTML(text, highlights)
      setHtml(newHtml)
      setError(highlights[0]?.message ?? null)
      setResult(Expr.evaluate(parsed, env))
    } catch (err: any) {
      parsedExprRef.current = null
      setError(err.message || "Invalid expression")
      setResult(null)
      const { html: fallbackHtml, spans } = renderHighlightedHTML(text, [])
      setHtml(fallbackHtml)
    }
  }, [])

  return (
    <div style={{ display: "flex", fontFamily: "sans-serif" }}>
      <style>{styles}</style>
      {/* Sidebar */}
      <div
        style={{ padding: "1rem", borderRight: "1px solid #ccc", width: 180 }}
      >
        <strong>Parameters</strong>
        <ul style={{ listStyle: "none", padding: 0 }}>
          {Object.entries(parameters).map(([id, param]) => (
            <li key={id} style={{ margin: "0.25rem 0" }}>
              <button
                style={{
                  background: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  padding: "0.25rem 0.5rem",
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                }}
                title={
                  param.type === "string" && param.options
                    ? `Options: ${param.options.join(", ")}`
                    : undefined
                }
                onClick={() => {
                  const el = inputRef.current
                  if (!el) return

                  const sel = window.getSelection()
                  const range = sel?.getRangeAt(0)

                  // ✅ Only allow insert if selection is inside the input div
                  if (!range || !el.contains(range.startContainer)) return

                  const textNode = document.createTextNode(param.name)
                  range.insertNode(textNode)
                  range.setStartAfter(textNode)
                  range.setEndAfter(textNode)
                  sel?.removeAllRanges()
                  sel?.addRange(range)

                  handleChange()
                }}
              >
                {param.name}
              </button>
            </li>
          ))}
        </ul>

        <div style={{ marginTop: "1rem" }}>
          <strong>Functions</strong>
          <ul style={{ listStyle: "none", padding: 0 }}>
            {unaryFunctions.map((fn) => (
              <li key={fn} style={{ margin: "0.25rem 0" }}>
                <button
                  style={{
                    background: "#fff3e0",
                    color: "#e65100",
                    fontWeight: "bold",
                    border: "1px solid #e65100",
                    borderRadius: 4,
                    padding: "0.25rem 0.5rem",
                    cursor: "pointer",
                    width: "100%",
                    textAlign: "left",
                  }}
                  onClick={() => {
                    const el = inputRef.current
                    if (!el) return
                    const sel = window.getSelection()
                    const range = sel?.getRangeAt(0)
                    const fnNode = document.createTextNode(`${fn}()`)
                    range?.insertNode(fnNode)
                    range?.setStart(fnNode, fn.length + 1)
                    range?.setEnd(fnNode, fn.length + 1)
                    sel?.removeAllRanges()
                    sel?.addRange(range!)
                    handleChange()
                  }}
                >
                  {fn}(…)
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, padding: "1rem" }}>
        <div
          ref={inputRef}
          spellCheck={false}
          contentEditable
          onInput={handleChange}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          style={{
            whiteSpace: "pre-wrap",
            fontFamily: "monospace",
            fontSize: "1rem",
            padding: "0.5rem",
            border: "1px solid #ccc",
            minHeight: "2rem",
            outline: "none",
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <div style={{ marginTop: "0.5rem" }}>
          {error ? (
            <span style={{ color: "red" }}>{error}</span>
          ) : (
            <span>Result: {result?.toString()}</span>
          )}
        </div>
        {onSubmit && parsedExprRef.current && !error && (
          <div style={{ marginTop: "1rem" }}>
            <button
              style={{
                background: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "4px",
                padding: "0.5rem 1rem",
                fontWeight: "bold",
                cursor: "pointer",
              }}
              onClick={() => onSubmit(parsedExprRef.current!)}
            >
              Set Expression
            </button>
          </div>
        )}
      </div>
      {hoveredTooltip && (
        <div
          className="tooltip"
          style={{
            left: hoveredTooltip.x,
            top: hoveredTooltip.y,
            position: "fixed",
          }}
        >
          {hoveredTooltip.message}
        </div>
      )}
    </div>
  )
}
